import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CRM_ANY, canAccess } from "../../pages/PermGuard";
import {
  LayoutDashboard, Users, Contact2, Package, Inbox, FileText,
  FilePlus2, ReceiptText, Receipt, CalendarClock, BarChart3, StickyNote,
  Settings as SettingsIcon, X, ClipboardList, Hammer, Factory,
  FlaskConical, Box, Cog, Truck, Boxes, Database, ChevronDown,
  ShieldCheck, ScrollText, UserCog, Upload, Wrench, AlertTriangle, Activity, PackagePlus, Building2,
  ScanSearch, Scale, Sparkles, Coins, ShoppingCart, ArrowLeftRight, ClipboardCheck, Settings2, Combine, Mail, Link2, Scissors, ScanLine, FileCheck2, FileStack, Camera, Wallet,
} from "lucide-react";

const NAV = [
  { to: "/home", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "dashboard", perm: "crm_dashboard.view" },
  { to: "/enquiries", label: "Enquiry Register", icon: Inbox, tid: "enquiries", perm: "enquiries.view" },
  { to: "/email-drafts", label: "Email drafts", icon: Mail, tid: "email-drafts", perm: "email_drafts.view" },
  { to: "/customers", label: "Customer Master", icon: Users, tid: "customers", perm: "customers.view" },
  { to: "/crm-cleanup", label: "Data Cleanup", icon: Combine, tid: "crm-cleanup", perm: "customers.view" },
  { to: "/contacts", label: "Contact Master", icon: Contact2, tid: "contacts", perm: "contacts.view" },
  { to: "/quotes/new", label: "Quote Generator", icon: FilePlus2, tid: "quote-generator", perm: "quotes.create" },
  { to: "/quotes", label: "Quote Register", icon: FileText, tid: "quote-register", perm: "quotes.view" },
  { to: "/pis", label: "PI Register", icon: ReceiptText, tid: "pi-register", perm: "pi.view" },
  { to: "/tax-invoices", label: "Tax Invoices", icon: Receipt, tid: "tax-invoices", perm: "tax_invoice.view" },
];

const PRODUCTION_NAV = [
  { to: "/production", label: "Production Dashboard", icon: Factory, end: true, tid: "production-dashboard", perm: "production_dashboard.view" },
  { to: "/sales-orders", label: "Sales Orders", icon: ClipboardList, tid: "sales-orders", perm: "sales_orders.view" },
  { to: "/tally-pairing", label: "Tally pairing", icon: Link2, tid: "production-tally-pairing", perm: "sales_orders.view" },
  { to: "/order-tracking", label: "Order Tracking", icon: ClipboardCheck, tid: "order-tracking", perm: "order_tracking.view" },
  { to: "/work-orders", label: "Work Orders", icon: Hammer, tid: "work-orders", perm: "work_orders.view" },
  { to: "/mes/planning", label: "Production Planning", icon: Cog, tid: "mes-planning", perm: "work_orders.view" },
  { to: "/inventory/compound-production", label: "Compound Production", icon: FlaskConical, tid: "compound-production", perm: "compound_production.view" },
  { to: "/mes/register", label: "Production Register", icon: ScrollText, tid: "mes-register", perm: "work_orders.view" },
  { to: "/mes/dashboard", label: "MES Dashboard", icon: Activity, tid: "mes-dashboard", perm: "work_orders.view" },
  { to: "/mes/finished-goods", label: "Finished Goods", icon: PackagePlus, tid: "mes-finished-goods", perm: "work_orders.view" },
  { to: "/finishing-challans", label: "Finishing Challans", icon: Scissors, tid: "finishing-challans", perm: "finishing_challans.view" },
  { to: "/mes/transfers", label: "Internal Stock Transfer", icon: ArrowLeftRight, tid: "mes-transfers", perm: "stock_transfer.view" },
  { to: "/mes/employees", label: "Employee Master", icon: UserCog, tid: "mes-employees", perm: "work_orders.view" },
];

const ACCOUNTS_NAV = [
  { to: "/accounts", label: "Ready for Billing", icon: Receipt, end: true, tid: "accounts-billing", perm: "accounts_dashboard.view" },
  { to: "/accounts/einvoice", label: "e-Invoice to do", icon: FileCheck2, tid: "accounts-einvoice", perm: "accounts_dashboard.view", badgeKey: "einvoice" },
  { to: "/tax-invoices", label: "Tax Invoices", icon: ReceiptText, tid: "accounts-tax-invoices", perm: "tax_invoice.view" },
  { to: "/tally-pairing", label: "Tally pairing", icon: Link2, tid: "tally-pairing", perm: "sales_orders.view" },
];

const DISPATCH_NAV = [
  { to: "/dispatch", label: "Dispatch Register", icon: Truck, end: true, tid: "dispatch", perm: "dispatch.view" },
  { to: "/dispatch/scan-load", label: "Scan to Load", icon: ScanLine, tid: "dispatch-scan-load", perm: "dispatch.view" },
  { to: "/dispatch/boxes", label: "Box Search / Trace", icon: Boxes, tid: "dispatch-boxes", perm: "dispatch.view" },
];

// Quality: in-plant QC stages (PDIR now; FPA, patrol, final lot inspection next), CAPA
const QUALITY_NAV = [
  { to: "/qc/dashboard", label: "QC Dashboard", icon: LayoutDashboard, tid: "qc-dashboard", perm: ["qc_pdir.view", "qc_fpa.view", "qc_patrol.view", "qc_final.view"], badgeKey: "qc_alerts" },
  { to: "/qc/patrol", label: "Patrol Due", icon: ClipboardCheck, tid: "qc-patrol", perm: "qc_patrol.view" },
  { to: "/qc/night", label: "Night Review", icon: ClipboardCheck, tid: "qc-night", perm: ["qc_fpa.approve", "qc_final.approve", "qc_patrol.view"] },
  { to: "/qc/fpa", label: "First-piece Approval", icon: ClipboardCheck, tid: "qc-fpa", perm: "qc_fpa.view" },
  { to: "/qc/final", label: "Final Lot Inspection", icon: ClipboardCheck, tid: "qc-final", perm: "qc_final.view" },
  { to: "/qc", label: "PDIR (Pre-Dispatch)", icon: ClipboardCheck, end: true, also: ["/qc/dispatch", "/qc/inspections"], tid: "qc-checklists", perm: "qc_pdir.view" },
  { to: "/qc/register", label: "QC Register", icon: ScrollText, tid: "qc-register", perm: "qc_pdir.view" },
  { to: "/qc/templates", label: "QC Templates", icon: FileText, tid: "qc-templates", perm: "qc_templates.view" },
  { to: "/qc/settings", label: "QC Settings", icon: Settings2, tid: "qc-settings", perm: "qc_settings.edit" },
  { to: "/capa", label: "CAPA", icon: AlertTriangle, tid: "capa", perm: "capa.view", badgeKey: "capa", not: ["/capa/templates"] },
  { to: "/capa/templates", label: "CAPA Templates", icon: FileText, tid: "capa-templates", perm: "capa.view" },
];
const QUALITY_KEYS = ["qc_pdir.view", "qc_templates.view", "qc_fpa.view", "qc_patrol.view", "qc_final.view", "qc_settings.edit", "capa.view"];

// Document Dispatch: plant papers (bills, LR copies, signed DCs) carried to the Head Office
const DOCS_NAV = [
  { to: "/doc-dispatch", label: "Dispatch Home", icon: FileStack, end: true, tid: "dd-home", perm: "doc_dispatch.view", badgeKey: "docs" },
  { to: "/doc-dispatch/upload", label: "Upload Document", icon: Camera, tid: "dd-upload", perm: "doc_dispatch.create" },
  { to: "/doc-dispatch/documents", label: "Documents", icon: FileText, tid: "dd-documents", perm: "doc_dispatch.view" },
  { to: "/doc-dispatch/challans", label: "Internal Challans", icon: Truck, tid: "dd-challans", perm: "doc_dispatch.view" },
  { to: "/doc-dispatch/petty-cash", label: "Petty Cash", icon: Wallet, tid: "dd-petty", perm: "doc_dispatch.view" },
];

const MASTER_DATA_NAV = [
  { to: "/products", label: "Product Master", icon: Package, tid: "products", perm: "products.view" },
  { to: "/recipe-master", label: "Recipe Master", icon: FlaskConical, tid: "recipe-master", perm: "recipe_master.view" },
  { to: "/mould-master", label: "Mould Master", icon: Box, tid: "mould-master", perm: "mould_master.view" },
  { to: "/machine-master", label: "Machine Master", icon: Cog, tid: "machine-master", perm: "machine_master.view" },
  { to: "/data-import", label: "Data Import Wizard", icon: Upload, tid: "data-import", perm: "data_import.view" },
];

const COSTING_NAV = [
  { to: "/compound-costing", label: "Compound Costing", icon: FlaskConical, tid: "compound-costing", perm: "compound_costing.view" },
  { to: "/product-costing", label: "Product Costing", icon: Package, tid: "product-costing", perm: "product_costing.view" },
];

const AI_ASSISTANT_NAV = [
  { to: "/ai/product-finder", label: "AI Product Finder", icon: ScanSearch, tid: "ai-product-finder", perm: "ai_product_finder.view" },
  { to: "/ai/weight-calculator", label: "AI Weight Calculator", icon: Scale, tid: "ai-weight-calculator", perm: "ai_weight_calculator.view" },
  { to: "/ai/mould-cost-estimator", label: "AI Tooling Engineer", icon: Coins, tid: "ai-tooling-engineer", perm: "ai_product_finder.view" },
];

const MAINTENANCE_NAV = [
  { to: "/maintenance", label: "Maintenance Dashboard", icon: Activity, end: true, tid: "maintenance-dashboard", perm: "maintenance_dashboard.view" },
  { to: "/breakdowns", label: "Breakdowns", icon: AlertTriangle, tid: "breakdowns", perm: "breakdowns.view" },
  { to: "/pm-schedule", label: "PM Schedule", icon: Wrench, tid: "pm-schedule", perm: "preventive_maintenance.view" },
];

const PROCUREMENT_NAV = [
  { to: "/procurement", label: "Procurement Dashboard", icon: Activity, end: true, tid: "procurement-dashboard", perm: ["procurement_dashboard.view", "goods_receipt.view"] },
  { to: "/grn", label: "Material Receipts (GRN)", icon: PackagePlus, tid: "grn", perm: "goods_receipt.view" },
  { to: "/vendors", label: "Vendor Master", icon: Building2, tid: "vendors", perm: "vendors.view" },
  { to: "/rm-stock", label: "RM Stock", icon: Boxes, tid: "rm-stock", perm: "rm_stock.view" },
];

const STORES_NAV_PROD = [
  { to: "/inventory/raw-materials", label: "Raw Material Inventory", icon: Boxes, tid: "raw-material-inventory", perm: "raw_material_inventory.view" },
  { to: "/inventory/physical-stock", label: "Physical Stock Adjustment", icon: ClipboardCheck, tid: "physical-stock", perm: "physical_stock.view" },
];

const STORES_NAV_PURCHASE = [
  { to: "/purchase/stock-dashboard", label: "Stock Dashboard", icon: Activity, tid: "inventory-dashboard", perm: "inventory_dashboard.view" },
  { to: "/purchase/raw-materials", label: "Raw Material Inventory", icon: Boxes, tid: "purchase-raw-material-inventory", perm: "raw_material_inventory.view" },
  { to: "/purchase/transfers", label: "Internal Stock Transfer", icon: ArrowLeftRight, tid: "stock-transfers", perm: "stock_transfer.view" },
  { to: "/purchase/recommendations", label: "Purchase Recommendations", icon: ShoppingCart, tid: "purchase-recommendations", perm: "purchase_recommendation.view" },
  { to: "/purchase/ledger", label: "Inventory Ledger", icon: ScrollText, tid: "inventory-ledger", perm: "inventory_ledger.view" },
];

const TOOLING_NAV = [
  { to: "/tooling", label: "Tooling Dashboard", icon: Wrench, tid: "tooling-dashboard", perm: "tooling_dashboard.view" },
];

const PURCHASE_NAV = [
  { to: "/purchase", label: "Purchase Indents", icon: ClipboardList, tid: "purchase-indents", perm: "purchase_indents.view" },
  { to: "/raw-materials", label: "Raw Material Master", icon: Boxes, tid: "raw-material-master", perm: "raw_material_master.view" },
];

const SECONDARY_NAV = [
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, tid: "followups", perm: "enquiries.view" },
  { to: "/reports", label: "Reports", icon: BarChart3, tid: "reports", perm: "reports.view" },
  { to: "/notes", label: "Notes", icon: StickyNote, tid: "notes", perm: CRM_ANY },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "settings", perm: "company_settings.view" },
];

const ADMIN_NAV = [
  { to: "/users", label: "User Management", icon: UserCog, tid: "user-management", perm: "user_management.view" },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, tid: "audit-log", perm: "audit_log.view" },
];

function useVisible() {
  const { has } = useAuth();
  return (items) => items.filter((i) => !i.perm || canAccess(has, i.perm));
}

function NavList({ items, onItemClick }) {
  const loc = useLocation();
  const visible = useVisible()(items);
  const einvoice = useEinvoiceCount(visible.some((i) => i.badgeKey === "einvoice"));
  const capaOverdue = useCapaOverdue(visible.some((i) => i.badgeKey === "capa"));
  const ddAlerts = useDdAlerts(visible.some((i) => i.badgeKey === "docs"));
  const qcAlerts = useQcAlerts(visible.some((i) => i.badgeKey === "qc_alerts"));
  if (visible.length === 0) return null;
  return (
    <ul className="space-y-0.5">
      {visible.map((item) => {
        const Icon = item.icon;
        const isActive = ((item.end ? loc.pathname === item.to : loc.pathname.startsWith(item.to))
          || (item.also || []).some((p) => loc.pathname.startsWith(p))) && !(item.not || []).some((p) => loc.pathname.startsWith(p));
        return (
          <li key={item.to}>
            <NavLink to={item.to} end={item.end} onClick={onItemClick}
              data-testid={`sidebar-nav-${item.tid}`}
              className={[
                "group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
                isActive ? "bg-white text-[#ab0000] border border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
              ].join(" ")}>
              <Icon className={isActive ? "text-[#ab0000]" : "text-slate-400 group-hover:text-slate-600"} size={18} strokeWidth={1.75} />
              <span>{item.label}</span>
              {item.badgeKey === "einvoice" && einvoice > 0 && (
                <span data-testid="sidebar-einvoice-badge" title={`${einvoice} bill(s) need e-invoice in Tally`}
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#ab0000] text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                  {einvoice > 99 ? "99+" : einvoice}
                </span>
              )}
              {item.badgeKey === "capa" && capaOverdue > 0 && (
                <span data-testid="sidebar-capa-badge" title={`${capaOverdue} CAPA(s) overdue`}
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#ab0000] text-white text-[10px] font-bold flex items-center justify-center">
                  {capaOverdue > 99 ? "99+" : capaOverdue}
                </span>
              )}
              {item.badgeKey === "docs" && ddAlerts > 0 && (
                <span data-testid="sidebar-dd-badge" title={`${ddAlerts} document alert(s) / approval(s)`}
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#ab0000] text-white text-[10px] font-bold flex items-center justify-center">
                  {ddAlerts > 99 ? "99+" : ddAlerts}
                </span>
              )}
              {item.badgeKey === "qc_alerts" && qcAlerts > 0 && (
                <span data-testid="sidebar-qc-alerts-badge" title={`${qcAlerts} QC alert(s): patrol failures / issues raised`}
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#ab0000] text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                  {qcAlerts > 99 ? "99+" : qcAlerts}
                </span>
              )}
              {item.soon && <span className="ml-auto text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 font-semibold">Soon</span>}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function MasterDataGroup({ onItemClick }) {
  const loc = useLocation();
  const visible = useVisible()(MASTER_DATA_NAV);
  const isInside = MASTER_DATA_NAV.some((i) => loc.pathname.startsWith(i.to));
  const [open, setOpen] = useState(isInside);
  useEffect(() => { if (isInside) setOpen(true); }, [isInside]);
  if (visible.length === 0) return null;
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} data-testid="sidebar-master-data-toggle"
        className={["w-full group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
          isInside ? "text-[#ab0000] font-semibold" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"].join(" ")}>
        <Database className={isInside ? "text-[#ab0000]" : "text-slate-400 group-hover:text-slate-600"} size={18} strokeWidth={1.75} />
        <span>Master Data</span>
        <ChevronDown size={15} className={"ml-auto transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && <div className="pl-3 mt-0.5"><NavList items={MASTER_DATA_NAV} onItemClick={onItemClick} /></div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{children}</div>;
}

function Workspace({ mode, onItemClick }) {
  const { has } = useAuth();
  const visible = useVisible();
  const adminVisible = visible(ADMIN_NAV);
  if (mode === "tooling") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Tooling Department</div>
        <NavList items={TOOLING_NAV} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "purchase") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Purchase</div>
        <NavList items={PURCHASE_NAV} onItemClick={onItemClick} />
        {visible(STORES_NAV_PURCHASE).length > 0 && <SectionLabel>Stores &amp; Inventory</SectionLabel>}
        <NavList items={STORES_NAV_PURCHASE} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "accounts") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Accounts</div>
        <NavList items={ACCOUNTS_NAV} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "dispatch") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Dispatch</div>
        <NavList items={DISPATCH_NAV} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "docs") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Document Dispatch</div>
        <NavList items={DOCS_NAV} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "quality") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Quality</div>
        <NavList items={QUALITY_NAV} onItemClick={onItemClick} />
      </>
    );
  }
  if (mode === "production") {
    return (
      <>
        <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Production</div>
        <NavList items={PRODUCTION_NAV} onItemClick={onItemClick} />
        {visible(MASTER_DATA_NAV).length > 0 && <SectionLabel>Master Data</SectionLabel>}
        <MasterDataGroup onItemClick={onItemClick} />
        {visible(COSTING_NAV).length > 0 && <SectionLabel>Costing</SectionLabel>}
        <NavList items={COSTING_NAV} onItemClick={onItemClick} />
        {visible(AI_ASSISTANT_NAV).length > 0 && <SectionLabel>AI Assistant</SectionLabel>}
        <NavList items={AI_ASSISTANT_NAV} onItemClick={onItemClick} />
        {visible(MAINTENANCE_NAV).length > 0 && <SectionLabel>Maintenance</SectionLabel>}
        <NavList items={MAINTENANCE_NAV} onItemClick={onItemClick} />
        {visible(PROCUREMENT_NAV).length > 0 && <SectionLabel>Procurement &amp; Stores</SectionLabel>}
        <NavList items={PROCUREMENT_NAV} onItemClick={onItemClick} />
        {visible(STORES_NAV_PROD).length > 0 && <SectionLabel>Stores &amp; Inventory</SectionLabel>}
        <NavList items={STORES_NAV_PROD} onItemClick={onItemClick} />
      </>
    );
  }
  return (
    <>
      <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Sales & CRM</div>
      <NavList items={NAV} onItemClick={onItemClick} />
      {adminVisible.length > 0 && (
        <>
          <SectionLabel>Administration</SectionLabel>
          <NavList items={ADMIN_NAV} onItemClick={onItemClick} />
        </>
      )}
      <SectionLabel>More</SectionLabel>
      <NavList items={SECONDARY_NAV} onItemClick={onItemClick} />
    </>
  );
}

const CRM_KEYS = ["crm_dashboard.view", "enquiries.view", "customers.view", "contacts.view",
  "quotes.view", "quotes.create", "pi.view", "tax_invoice.view", "email_drafts.view", "reports.view", "user_management.view", "audit_log.view"];
const PROD_KEYS = ["production_dashboard.view", "sales_orders.view", "work_orders.view", "products.view",
  "recipe_master.view", "raw_material_master.view", "mould_master.view", "machine_master.view", "data_import.view",
  "compound.view", "production.view", "production_qc.view", "finishing.view", "finishing_challans.view",
  "compound_costing.view", "product_costing.view", "ai_product_finder.view", "ai_weight_calculator.view",
  "maintenance_dashboard.view", "breakdowns.view", "preventive_maintenance.view",
  "procurement_dashboard.view", "goods_receipt.view", "vendors.view", "rm_stock.view",
  "raw_material_inventory.view", "compound_production.view", "physical_stock.view"];
const PURCHASE_KEYS = ["purchase_indents.view", "inventory_dashboard.view", "raw_material_inventory.view",
  "stock_transfer.view", "purchase_recommendation.view", "inventory_ledger.view", "raw_material_master.view"];

// Workspaces this user may open, in menu order
function useAllowedModes() {
  const { has } = useAuth();
  const any = (keys) => keys.some((k) => has(k));
  return [
    any(CRM_KEYS) && "crm", any(PROD_KEYS) && "production", has("tooling_dashboard.view") && "tooling",
    any(PURCHASE_KEYS) && "purchase", has("accounts_dashboard.view") && "accounts", has("dispatch.view") && "dispatch",
    any(QUALITY_KEYS) && "quality", has("doc_dispatch.view") && "docs",
  ].filter(Boolean);
}

/** CAPAs past their target date or with an overdue action (checked every 5 minutes). */
export function useCapaOverdue(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/capa/summary").then((r) => { if (alive) setN(r.data.overdue || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return n;
}

/** Document Dispatch alerts + papers waiting for this user's approval (checked every 5 minutes). */
export function useDdAlerts(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/doc-dispatch/alerts/count").then((r) => { if (alive) setN(r.data.total || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return n;
}

/** Open QC alerts of the user's plants (patrol failures, issues from the night review), checked every 2 minutes. */
export function useQcAlerts(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/qc/alerts").then((r) => { if (alive) setN((r.data || []).length); }).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return n;
}

/** Bills waiting for their e-invoice in Tally (checked every minute). */
export function useEinvoiceCount(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/tally/einvoice-queue").then((r) => { if (alive) setN(r.data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return n;
}

function WorkspaceSwitcher({ mode, setMode }) {
  const { has } = useAuth();
  const anyPerm = (keys) => keys.some((k) => has(k));
  const showPurchase = anyPerm(PURCHASE_KEYS);
  const [needsPurchase, setNeedsPurchase] = useState(0);
  const einvoiceCount = useEinvoiceCount(has("accounts_dashboard.view"));
  const capaOverdue = useCapaOverdue(has("capa.view"));
  const ddAlerts = useDdAlerts(has("doc_dispatch.view"));
  useEffect(() => {
    if (!showPurchase) return undefined;
    let alive = true;
    const fetchCount = () => api.get("/inventory/alerts").then((r) => { if (alive) setNeedsPurchase(r.data.needs_purchase || 0); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [showPurchase]);
  const ALL = [
    { key: "crm", label: "CRM", icon: Users, tid: "workspace-crm", show: anyPerm(CRM_KEYS) },
    { key: "production", label: "Production", icon: Factory, tid: "workspace-production", show: anyPerm(PROD_KEYS) },
    { key: "tooling", label: "Tooling", icon: Wrench, tid: "workspace-tooling", show: has("tooling_dashboard.view") },
    { key: "purchase", label: "Purchase", icon: ShoppingCart, tid: "workspace-purchase", show: showPurchase },
    { key: "accounts", label: "Accounts", icon: Receipt, tid: "workspace-accounts", show: has("accounts_dashboard.view") },
    { key: "dispatch", label: "Dispatch", icon: Truck, tid: "workspace-dispatch", show: has("dispatch.view") },
    { key: "quality", label: "Quality", icon: ClipboardCheck, tid: "workspace-quality", show: anyPerm(QUALITY_KEYS) },
    { key: "docs", label: "Documents", icon: FileStack, tid: "workspace-docs", show: has("doc_dispatch.view") },
  ];
  const TABS = ALL.filter((t) => t.show);
  if (TABS.length <= 1) return null;
  return (
    <div className={"grid gap-1.5 p-1 rounded-lg bg-slate-100 border border-slate-200 " + (TABS.length >= 5 ? "grid-cols-3" : TABS.length === 4 ? "grid-cols-4" : TABS.length === 3 ? "grid-cols-3" : "grid-cols-2")} data-testid="workspace-switcher">
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = mode === t.key;
        const badge = t.key === "purchase" ? needsPurchase : t.key === "accounts" ? einvoiceCount : t.key === "quality" ? capaOverdue : t.key === "docs" ? ddAlerts : 0;
        return (
          <button key={t.key} onClick={() => setMode(t.key)} data-testid={t.tid}
            className={[
              "relative flex flex-col items-center justify-center gap-1 py-2.5 rounded-md text-[11px] font-semibold transition-all",
              active ? "bg-white text-[#ab0000] shadow-[0_1px_2px_rgba(0,0,0,0.06)]" : "text-slate-500 hover:text-slate-800",
            ].join(" ")}>
            {badge > 0 && (
              <span data-testid={`${t.key}-alert-badge`} title={t.key === "accounts" ? `${badge} bill(s) need e-invoice in Tally` : t.key === "quality" ? `${badge} CAPA(s) overdue` : t.key === "docs" ? `${badge} document alert(s) / approval(s)` : `${badge} material(s) need purchase`}
                className="absolute top-1 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#ab0000] text-white text-[10px] font-bold flex items-center justify-center leading-none animate-pulse">
                {badge > 99 ? "99+" : badge}
              </span>
            )}
            <Icon size={17} strokeWidth={1.75} className={active ? "text-[#ab0000]" : "text-slate-400"} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

const PRODUCTION_PATHS = ["/production", "/sales-orders", "/order-tracking", "/work-orders", "/production-planning", "/compound-batches", "/production-jobs", "/mes", "/products", "/recipe-master", "/mould-master", "/machine-master", "/machines", "/data-import", "/compound-costing", "/product-costing", "/maintenance", "/breakdowns", "/pm-schedule", "/procurement", "/grn", "/vendors", "/rm-stock", "/inventory", "/quality-control", "/finishing", "/finishing-challans", "/ai/product-finder", "/ai/weight-calculator", "/ai/mould-cost-estimator"];
const matchAny = (p, paths) => paths.some((x) => p === x || p.startsWith(x + "/"));
const isProductionPath = (p) => matchAny(p, PRODUCTION_PATHS);
const modeForPath = (p) =>
  matchAny(p, ["/accounts"]) ? "accounts"
  : matchAny(p, ["/dispatch"]) ? "dispatch"
  : matchAny(p, ["/qc", "/capa"]) ? "quality"
  : matchAny(p, ["/doc-dispatch"]) ? "docs"
  : matchAny(p, ["/tooling"]) ? "tooling"
  : matchAny(p, ["/purchase", "/raw-materials"]) ? "purchase"
  : isProductionPath(p) ? "production" : "crm";

// Every link each workspace's menu shows. A page that sits in more than one menu
// (Tax Invoices, Tally pairing) keeps whichever workspace you clicked it from.
const MODE_NAVS = {
  crm: [NAV, ADMIN_NAV, SECONDARY_NAV],
  production: [PRODUCTION_NAV, MASTER_DATA_NAV, COSTING_NAV, AI_ASSISTANT_NAV, MAINTENANCE_NAV, PROCUREMENT_NAV, STORES_NAV_PROD],
  tooling: [TOOLING_NAV],
  purchase: [PURCHASE_NAV, STORES_NAV_PURCHASE],
  accounts: [ACCOUNTS_NAV],
  dispatch: [DISPATCH_NAV],
  quality: [QUALITY_NAV],
  docs: [DOCS_NAV],
};
const modeHasPath = (mode, p) =>
  (MODE_NAVS[mode] || []).some((items) => items.some((i) => (i.end ? p === i.to : matchAny(p, [i.to]))));

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <img src="/sahara-logo.png" alt="Sahara Rubber" className="w-10 h-10 object-contain" />
      <div>
        <div className="font-display font-bold tracking-tight text-slate-900">Sahara Rubber</div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wider">Business OS</div>
      </div>
    </div>
  );
}

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { has } = useAuth();
  const allowed = useAllowedModes();
  // A page outside the user's workspaces (e.g. /home) opens their first workspace, not CRM.
  const modeFor = (p) => {
    const m = modeForPath(p);
    return allowed.length && !allowed.includes(m) ? allowed[0] : m;
  };
  const [mode, setModeState] = useState(() => modeFor(window.location.pathname));
  const allowedKey = allowed.join();

  useEffect(() => {
    // Stay in the current workspace when the page is in its menu; only switch when it isn't.
    setModeState((m) => (modeHasPath(m, loc.pathname) && allowed.includes(m) ? m : modeFor(loc.pathname)));
  }, [loc.pathname, allowedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = (next) => {
    setModeState(next);
    const purchaseDest = has("purchase_indents.view") ? "/purchase"
      : has("inventory_dashboard.view") ? "/purchase/stock-dashboard"
      : has("raw_material_inventory.view") ? "/purchase/raw-materials" : "/purchase";
    const dest = { production: "/production", tooling: "/tooling", purchase: purchaseDest, accounts: "/accounts", dispatch: "/dispatch", quality: has("qc_pdir.view") ? "/qc" : has("qc_fpa.view") ? "/qc/fpa" : has("qc_final.view") ? "/qc/final" : has("qc_templates.view") ? "/qc/templates" : "/capa", crm: "/home" }[next];
    if (modeForPath(loc.pathname) !== next) nav(dest);
  };

  return (
    <>
      <aside data-testid="app-sidebar" className="hidden md:flex md:flex-col w-[260px] shrink-0 border-r border-slate-200 bg-[#f8fafc] h-screen sticky top-0">
        <div className="px-6 py-5 border-b border-slate-200"><Brand /></div>
        <div className="px-3 pt-4"><WorkspaceSwitcher mode={mode} setMode={setMode} /></div>
        <nav className="flex-1 overflow-y-auto py-4 px-3"><Workspace mode={mode} /></nav>
        <div className="px-4 py-4 border-t border-slate-200 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} /> v1.0 · Secured</div>
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"></div>
          <aside onClick={(e) => e.stopPropagation()} className="relative w-[78%] max-w-[300px] bg-[#f8fafc] h-full flex flex-col animate-fade-up shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <Brand />
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="mobile-sidebar-close"><X size={18}/></button>
            </div>
            <div className="px-3 pt-4"><WorkspaceSwitcher mode={mode} setMode={setMode} /></div>
            <nav className="flex-1 overflow-y-auto py-4 px-3"><Workspace mode={mode} onItemClick={() => setMobileOpen(false)} /></nav>
          </aside>
        </div>
      )}
    </>
  );
}
